import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    json: String,
    expandLevel: Number
  }
  static targets = [
    "json",
    "itemTemplate",
    "nullTemplate",
    "arrayTemplate",
    "objectTemplate",
    "containerTemplate",
  ]

  connect() {
    const json = JSON.parse(this.jsonValue)
    this.jsonTarget.innerHTML = this.jsonViewer(json, {
      expand: {
        level: {
          lessThanOrEqual: this.optionExpandLevel
        }
      }
    })
  }

  /**
   * Get the expand level value
   * @returns {Number} - The expand level value
   */
  get optionExpandLevel() {
    if (this.hasExpandLevelValue) {
      return this.expandLevelValue
    }
    return 1
  }

  /**
   * JSON Viewer
   *
   * @param {Object} json - JSON object to be displayed
   * @param {Object} options - Options for the JSON viewer
   * @param {Object} options.expand - Options for expanding the JSON nodes
   * @param {Object} options.expand.level - Options for expanding the JSON nodes based on level
   * @param {Number} options.expand.level.lessThanOrEqual - Expand JSON nodes less than or equal to this level
   * @returns {String} - The JSON object as HTML string
   */
  jsonViewer(json, options = {}) {
    const TEMPLATES = {
      item: this.itemTemplateTarget,
      null: this.nullTemplateTarget,
      array: this.arrayTemplateTarget,
      object: this.objectTemplateTarget,
      container: this.containerTemplateTarget,
    };

    /**
      * Renders a template with the given data
      *
      * @usage renderTemplate('<div>${name}</div>', { name: 'John Doe' })
      * @param {HTMLElement} templateTarget - The template to render
      * @param {Object} data - The data to render
      * @returns {String} - The rendered template as HTML string
      */
    function renderTemplate(templateTarget, data) {
      return templateTarget.innerHTML.replace(/\${(\w*)}/g, (_pattern, key) => {
        return data.hasOwnProperty(key) ? data[key] : "";
      });
    }

    /**
      * Creates an item
      *
      * @param {String} key - The key of the item
      * @param {String} value - The value of the item
      * @param {String} type - The type of the item
      * @returns {String} - The item as HTML string
      */
    function createItem(key, value, type, path, _level) {
      const template = type === "null" ? TEMPLATES.null : TEMPLATES.item;
      return renderTemplate(template, {
        key: escapeHtml(key),
        value: escapeHtml(value),
        type: type,
        path: escapeHtml(path),
        copy: escapeAttribute(value)
      });
    }

    /**
     * Escapes HTML
     *
     * @param {String} unsafe - The unsafe HTML
     * @returns {String} - The escaped HTML
     */
    function escapeHtml(unsafe) {
      const div = document.createElement("div");
      div.textContent = unsafe;
      return div.innerHTML;
    }

    /**
      * Creates a collapsible item
      *
      * @param {String} key - The key of the item
      * @param {String} value - The value of the item
      * @param {String} type - The type of the item
      * @param {String} children - The children of the item as HTML string
      * @returns {String} - The collapsible item as HTML string
      */
    function createCollapsibleItem(key, value, type, children, path, level) {
      let expand = options.expand.level.lessThanOrEqual >= level;

      switch (type) {
        case "array":
          return setExpand(
            renderTemplate(TEMPLATES.array, {
              key: escapeHtml(key),
              children: children,
              path: escapeHtml(path),
              copy: escapeAttribute(value),
              type: "array"
            }),
            expand
          );
        case "object":
          return setExpand(
            renderTemplate(TEMPLATES.object, {
              key: escapeHtml(key),
              children: children,
              path: escapeHtml(path),
              copy: escapeAttribute(value),
              type: "object"
            }),
            expand
          );
        default:
          return createItem(key, value, type, path, level);
      }
    }

    function escapeAttribute(unsafe) {
      const serializedValue = JSON.stringify(unsafe);

      return serializedValue
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }

    function setExpand(collapsible, expand) {
      const tmpNode = document.createElement("div");
      tmpNode.innerHTML = collapsible;
      if (expand) {
        tmpNode.querySelector(".json__toggle").setAttribute("checked", "checked");
      } else {
        tmpNode.querySelector(".json__toggle").removeAttribute("checked");
      }
      return tmpNode.innerHTML;
    }

    /**
     * Handles children of an object
     *
     * @param {String} key - The key of the item
     * @param {Array|Object} value - The value of the item
     * @returns {String} - HTML string
     */
    function handleChildren(key, value, type, path, level) {
      let html = "";

      for (const item in value) {
        html += handleItem(item, value[item], path, value, level);
      }

      return createCollapsibleItem(key, value, type, html, path, level);
    }

    /**
     * Handles an item
     *
     * @param {String} key - The key of the item
     * @param {Any} value - The value of the item
     * @returns {String} - HTML string
     */
    function handleItem(key, value, path = "", thisParent, level = 0) {
      level = level + 1;

      const isArray = Array.isArray(thisParent);
      let type = Array.isArray(value) ? "array" : typeof value;
      type = value === null ? "null" : type;

      let itemPath;
      if (!path) {
        if (isArray) {
          itemPath = `[${key}]`;
        } else {
          itemPath = `.${key}`;
        }
      } else {
        if (isArray) {
          itemPath = `${path}[${key}]`;
        } else {
          itemPath = `${path}.${key}`;
        }
      }

      if (type === "object" || type === "array") {
        return handleChildren(key, value, type, itemPath, level);
      }

      return createItem(key, value, type, itemPath, level);
    }

    /**
      * Parses an object
      *
      * @param {Object} obj - The JSON object to parse
      * @returns {String} - The parsed object as HTML string
      */
    function parseObject(obj) {
      let result = "";

      for (const key in obj) {
        result += handleItem(key, obj[key], "", obj, 0);
      }

      return renderTemplate(TEMPLATES.container, { content: result });
    }

    return parseObject(json);
  }
}
